import { forwardRef, memo } from 'react';
import type { ComponentPropsWithoutRef, ReactElement, ReactNode } from 'react';

import { useActionContext, useIsDesktop, useScrollContext } from '@/components/Header/HeaderContext';
import { cx } from '@/utils/cx';
import { pickDataAttrs } from '@/utils/pick-data-attrs';
import { useCoreUIDataContext } from '@/widgets/CoreUIDataProvider/context';
import { useCoreUIDataEntry } from '@/widgets/CoreUIDataProvider/CoreUIDataProvider';

import { DefaultBrandLogo } from './DefaultBrandLogo';
import styles from './HeaderLogoWidget.module.scss';

import type { VisibleOn } from '@/components/Header/types';

/**
 * `Header.Logo` dot-notation — widget-backed.
 *
 * # 동작 매트릭스
 *
 * | 입력 | 결과 |
 * |---|---|
 * | `logo` 명시 | logo 그대로 (host override, BC) — host 가 transition/scroll/business 분기 직접 |
 * | `logo` 미명시 + Provider + bi success | `useIsDesktop ? defaultImage.pc : defaultImage.mobile`. 해당 viewport URL 누락 시 DefaultBrandLogo |
 * | `logo` 미명시 + Provider + idle/loading/error | DefaultBrandLogo (inline SVG) |
 * | `logo` 미명시 + Provider 없음 | DefaultBrandLogo + dev warn |
 *
 * # 렌더
 *
 * - `href` 명시 → `<a>` (SEO 친화 link graph)
 * - `href` 미명시 → `<button>`
 * - 모든 props 는 button/anchor 의 표준 prop 또는 wrapping `<span>` 의 스타일에 적용
 *
 * # 사용
 *
 * ```tsx
 * <CoreUIDataProvider env="prod" platform="Web">
 *   <Header>
 *     <Header.Top>
 *       <Header.Left>
 *         <Header.Logo />  // 자동 fetch + DefaultBrandLogo fallback
 *       </Header.Left>
 *     </Header.Top>
 *   </Header>
 * </CoreUIDataProvider>
 * ```
 */
export interface HeaderLogoProps extends Omit<ComponentPropsWithoutRef<'button'>, 'children'> {
  /**
   * 로고 아이콘. **권장 사용 순서**:
   *
   * 1. **미지정** (권장) — `<CoreUIDataProvider>` 안의 bi 응답 자동 fetch → `useIsDesktop` 분기로 URL 결정
   * 2. **URL string** — 호스트가 URL 을 직접 갖고 있을 때 string 으로 명시. widget default 와 동일하게 `<img>` 로 렌더
   * 3. **ReactElement** (advanced) — 호스트 자체 이미지 컴포넌트 강제 사용. 대부분의 케이스에선 1·2 로 충분
   *
   * 명시 (2 or 3) 시 widget 의 자동 fetch / DefaultBrandLogo fallback 모두 skip — host override.
   * Provider 부재 / fetch 실패 / URL 누락 시 DefaultBrandLogo (inline SVG).
   */
  logo?: ReactElement | string;
  /**
   * 로고 크기 명시적 override.
   *
   * 미지정 시 Header `variant` 가 크기를 결정 (default: 46.5×17 모바일, hero: 60×22 모바일, PC 64×24 고정).
   * 명시 시 variant 보다 강하게 적용 — CSS inline style 로 iconWrapper 치수를 직접 덮어씀.
   *
   * - `"medium"` — 46.5×17 (모바일 default 동치). PC 는 항상 64×24 고정이라 영향 없음.
   * - `"large"` — 60×22 (모바일 hero 동치). PC 는 항상 64×24 고정이라 영향 없음.
   */
  size?: 'medium' | 'large';
  /** 식별자 — aria-label 자동생성, onAction 인자, data-action attribute */
  action?: string;
  /**
   * navigation URL. 있으면 `<a href>` 로 렌더 (SEO 친화: 크롤러 link graph 포함),
   * 없으면 `<button>` 으로 렌더 (기존 동작 호환).
   *
   * `<a>` 의 default navigation 은 React onClick 콜백 호출 후 자동으로 발생합니다.
   * SPA navigation 이 필요하면 onClick (또는 onAction) 핸들러에서 `e.preventDefault()` 후 자체 router 호출.
   */
  href?: string;
  /** `<a>` 모드 보조 — `target="_blank"` 등. button 모드에선 무시됨 */
  target?: string;
  /** `<a>` 모드 보조 — `rel="noopener noreferrer"` 등. button 모드에선 무시됨 */
  rel?: string;
  /** 스크롤 시에만 표시 (CSS class 토글, DOM 항상 유지) */
  showOnScroll?: boolean;
  /** 특정 디바이스에서만 노출 */
  visibleOn?: VisibleOn;
  /** onAction 콜백에 전달할 메타데이터 */
  meta?: Record<string, unknown>;
}

/** size prop → iconWrapper inline style. variant CSS 보다 specificity 높은 inline style 로 강제 override. */
const SIZE_STYLE: Record<NonNullable<HeaderLogoProps['size']>, React.CSSProperties> = {
  medium: { width: '46.5px', height: '17px' },
  large: { width: '60px', height: '22px' },
};

export const HeaderLogoWidget = memo(
  forwardRef<HTMLButtonElement, HeaderLogoProps>(
    (
      {
        logo,
        size,
        action = 'logo',
        href,
        target,
        rel,
        meta = {},
        onClick,
        className,
        visibleOn,
        showOnScroll,
        'aria-label': ariaLabel,
        ...rest
      },
      ref,
    ) => {
      const ctx = useCoreUIDataContext();
      const isDesktop = useIsDesktop();
      const { onAction } = useActionContext();
      const { isScrolled } = useScrollContext();

      // host 가 logo 명시 → fetch skip. Provider 부재 시도 enabled=false 로 hook 내부 warn 미발생.
      // BI_AUTO_FETCH_ENABLED — bi endpoint 미확정 동안 fetch 차단용 임시 플래그 (모듈 상단 참조).

      const shouldFetch = ctx !== null && logo === undefined;
      const { entry } = useCoreUIDataEntry('bi', { enabled: shouldFetch });

      if (process.env.NODE_ENV !== 'production' && ctx === null && logo === undefined) {
        // eslint-disable-next-line no-console
        console.warn(
          '[core-ui] Header.Logo: no <CoreUIDataProvider> ancestor and no `logo` prop. ' +
            'Falling back to DefaultBrandLogo. Provide one of: wrap in <CoreUIDataProvider> (auto-fetch), or pass `logo` prop directly.',
        );
      }

      // 우선순위: host logo > Provider bi success > DefaultBrandLogo
      let resolvedLogo: ReactElement | string;
      if (logo !== undefined) {
        resolvedLogo = logo;
      } else {
        const bi = entry.status === 'success' ? entry.data : null;
        // eslint-disable-next-line no-nested-ternary
        const url = bi !== null ? (isDesktop ? bi.defaultImage.pc : bi.defaultImage.mobile) : undefined;
        resolvedLogo = url ?? <DefaultBrandLogo />;
      }

      const isHidden = showOnScroll && !isScrolled;
      const resolvedAriaLabel = ariaLabel ?? '홈';
      const logoIcon: ReactNode =
        typeof resolvedLogo === 'string' ? <img alt="로고" aria-hidden="true" src={resolvedLogo} /> : resolvedLogo;
      // PC 는 항상 64×24 고정 — size prop 영향 없음 (variant 와 일관). 모바일에서만 inline style 강제 override.
      const iconWrapperStyle = size !== undefined && !isDesktop ? SIZE_STYLE[size] : undefined;

      const handleClick = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        onClick?.(e as React.MouseEvent<HTMLButtonElement>);

        if (!e.defaultPrevented) {
          onAction?.(action, meta, e as React.MouseEvent);
        }
      };

      // href 가 있으면 <a> 로 렌더 — 크롤러가 link graph 인식 가능. SEO 친화.
      // href 가 없으면 <button> — toggle / dialog open 등 non-navigation 의도.
      if (href) {
        return (
          <a
            {...pickDataAttrs(rest)}
            aria-hidden={isHidden || undefined}
            aria-label={resolvedAriaLabel}
            className={cx(styles.logo, isHidden && styles.hidden, className)}
            data-action={action}
            data-core-ui-visible-on={visibleOn}
            href={href}
            onClick={handleClick}
            ref={ref as unknown as React.Ref<HTMLAnchorElement>}
            rel={rel}
            suppressHydrationWarning={rest.suppressHydrationWarning}
            tabIndex={isHidden ? -1 : undefined}
            target={target}
          >
            <span className={styles.iconWrapper} style={iconWrapperStyle}>
              {logoIcon}
            </span>
          </a>
        );
      }

      return (
        <button
          aria-hidden={isHidden || undefined}
          aria-label={resolvedAriaLabel}
          className={cx(styles.logo, isHidden && styles.hidden, className)}
          data-action={action}
          data-core-ui-visible-on={visibleOn}
          onClick={handleClick}
          ref={ref}
          tabIndex={isHidden ? -1 : undefined}
          type="button"
          {...rest}
        >
          <span className={styles.iconWrapper} style={iconWrapperStyle}>
            {logoIcon}
          </span>
        </button>
      );
    },
  ),
);

HeaderLogoWidget.displayName = 'Header.Logo';
